import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  CheckCircle2, Circle, FileText, Shield, Upload, Search,
  Sparkles, ArrowRight, Stethoscope, Clock, AlertTriangle,
} from "lucide-react";
import BAAgreement from "./BAAgreement";
import CredentialSubmissionForm from "./CredentialSubmissionForm";
import { useToast } from "@/hooks/use-toast";

import Navbar from "./Navbar";

const isRlsError = (err: any): boolean => {
  if (!err) return false;
  const code = String(err.code ?? err.statusCode ?? err.status ?? "").toLowerCase();
  const msg = String(err.message ?? "").toLowerCase();
  if (code === "42501" || code === "pgrst301" || code === "403") return true;
  return (
    msg.includes("row-level security") ||
    msg.includes("row level security") ||
    msg.includes("violates row-level") ||
    msg.includes("permission denied") ||
    msg.includes("new row violates")
  );
};

interface CredentialStatus {
  credential_status: string;
  rejection_reason: string | null;
  baa_accepted_at: string | null;
  npi_verified: boolean;
  npi_number: string;
  medical_license_number: string;
  license_state: string;
  license_expiry: string;
  dea_number: string | null;
  dea_expiry: string | null;
  specialty: string | null;
  bio: string | null;
  w9_status: string;
  npi_verification_data: any;
}

interface ProviderOnboardingGateProps {
  children: React.ReactNode;
}

const ProviderOnboardingGate = ({ children }: ProviderOnboardingGateProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [creds, setCreds] = useState<CredentialStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [verifyingNpi, setVerifyingNpi] = useState(false);
  const [signingBaa, setSigningBaa] = useState(false);
  const [uploadingW9, setUploadingW9] = useState(false);

  const fetchCreds = async () => {
    if (!user) return;
    const { data } = await supabase
      .from("provider_credentials" as any)
      .select("credential_status, rejection_reason, baa_accepted_at, npi_verified, npi_number, medical_license_number, license_state, license_expiry, dea_number, dea_expiry, specialty, bio, w9_status, npi_verification_data")
      .eq("user_id", user.id)
      .single();
    setCreds(data as any);
    setLoading(false);
  };

  useEffect(() => { fetchCreds(); }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // No credentials at all — shouldn't happen if they registered
  if (!creds) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="container mx-auto px-4 pt-24 pb-16 max-w-lg text-center">
          <Stethoscope size={40} className="mx-auto text-muted-foreground mb-4" />
          <h1 className="font-display text-2xl text-foreground mb-2">No Credentials Found</h1>
          <p className="text-muted-foreground">Please complete provider registration first.</p>
        </div>
      </div>
    );
  }

  // If approved, show the full dashboard
  if (creds.credential_status === "approved") {
    return <>{children}</>;
  }

  // Show onboarding view for non-approved providers
  const steps = [
    { id: "credentials", label: "Credentials Submitted", done: true, icon: FileText },
    { id: "npi", label: "NPI Verified", done: creds.npi_verified, icon: Search },
    { id: "baa", label: "BAA Signed", done: !!creds.baa_accepted_at, icon: Shield },
    { id: "w9", label: "W-9 Uploaded", done: creds.w9_status === "uploaded" || creds.w9_status === "verified", icon: Upload },
    { id: "review", label: "Admin Approval", done: creds.credential_status === "approved", icon: CheckCircle2 },
  ];

  const completedCount = steps.filter((s) => s.done).length;
  const progressPercent = Math.round((completedCount / steps.length) * 100);

  const handleVerifyNpi = async () => {
    setVerifyingNpi(true);
    try {
      const { data, error } = await supabase.functions.invoke("verify-npi", {
        body: { npi_number: creds.npi_number },
      });
      if (error) throw error;
      if (data?.verified) {
        toast({ title: "NPI Verified!", description: `Found: ${data.provider_name}` });
      } else {
        toast({ title: "NPI Not Verified", description: data?.message || "NPI not found in NPPES registry.", variant: "destructive" });
      }
      await fetchCreds();
    } catch (err: any) {
      toast({ title: "Verification failed", description: err.message, variant: "destructive" });
    } finally {
      setVerifyingNpi(false);
    }
  };

  const handleSignBaa = async () => {
    if (!user) return;
    setSigningBaa(true);
    try {
      const { error } = await supabase
        .from("provider_credentials" as any)
        .update({
          baa_accepted_at: new Date().toISOString(),
          baa_ip_address: "client", // In production, capture from headers
        })
        .eq("user_id", user.id);
      if (error) throw error;
      toast({ title: "BAA Signed", description: "Business Associate Agreement accepted." });
      await fetchCreds();
    } catch (err: any) {
      if (isRlsError(err)) {
        // Note: the global RLS interceptor (src/integrations/supabase/rls-interceptor.ts)
        // already logs this to admin_error_log — no manual logError() needed here.
        toast({
          title: "Permission needed",
          description:
            "We couldn't record your BAA signature. Your account doesn't have permission yet — please contact support@elarahealth.life and we'll get you set up right away.",
          variant: "destructive",
        });
      } else {
        toast({ title: "Couldn't sign BAA", description: err.message, variant: "destructive" });
      }
    } finally {
      setSigningBaa(false);
    }
  };

  const handleW9Upload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    setUploadingW9(true);
    try {
      const filePath = `${user.id}/w9-${Date.now()}.${file.name.split(".").pop()}`;
      const { error: uploadError } = await supabase.storage
        .from("w9-uploads")
        .upload(filePath, file);
      if (uploadError) throw uploadError;

      // Update contractor_tax_info
      const { error: taxError } = await supabase
        .from("contractor_tax_info")
        .upsert({
          user_id: user.id,
          w9_file_url: filePath,
          w9_collected_at: new Date().toISOString(),
          filing_status: "w9_uploaded",
        }, { onConflict: "user_id" });
      if (taxError) throw taxError;

      // Update provider_credentials w9_status
      const { error: credError } = await supabase
        .from("provider_credentials" as any)
        .update({ w9_status: "uploaded" })
        .eq("user_id", user.id);
      if (credError) throw credError;

      toast({ title: "W-9 Uploaded", description: "Your W-9 form has been received." });
      await fetchCreds();
    } catch (err: any) {
      if (isRlsError(err)) {
        // Global RLS interceptor logs this — no manual logError() needed.
        toast({
          title: "Permission needed",
          description:
            "We couldn't save your W-9. Your account doesn't have permission yet — please contact support@elarahealth.life and we'll get you set up right away.",
          variant: "destructive",
        });
      } else {
        toast({ title: "Upload failed", description: err.message, variant: "destructive" });
      }
    } finally {
      setUploadingW9(false);
    }
  };

  const statusMessage = creds.credential_status === "pending_review"
    ? "Your application is under review"
    : creds.credential_status === "rejected"
      ? "Your application was not approved"
      : "Complete the steps below to continue";

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="container mx-auto px-4 pt-24 pb-16 max-w-2xl">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Stethoscope size={20} className="text-primary" />
          </div>
          <div>
            <h1 className="font-display text-3xl text-foreground">Provider Onboarding</h1>
            <p className="text-muted-foreground mt-0.5">{statusMessage}</p>
          </div>
        </div>

        {/* Progress */}
        <Card className="border-primary/20 bg-primary/5 mb-8">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <Sparkles size={18} className="text-primary" />
              <h3 className="font-display text-lg text-foreground">Onboarding Progress</h3>
            </div>
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-muted-foreground">{completedCount} of {steps.length} complete</span>
              <span className="font-medium text-primary">{progressPercent}%</span>
            </div>
            <Progress value={progressPercent} className="h-2 mb-4" />

            <div className="space-y-3">
              {steps.map((step) => (
                <div
                  key={step.id}
                  className={`flex items-center gap-3 rounded-lg px-4 py-3 ${
                    step.done
                      ? "bg-primary/10 opacity-70"
                      : "bg-card border border-border"
                  }`}
                >
                  {step.done ? (
                    <CheckCircle2 size={20} className="text-primary shrink-0" />
                  ) : (
                    <Circle size={20} className="text-muted-foreground shrink-0" />
                  )}
                  <span className={`text-sm font-medium flex-1 ${step.done ? "line-through text-muted-foreground" : "text-foreground"}`}>
                    {step.label}
                  </span>
                  {step.done && <Badge variant="secondary" className="text-xs">Done</Badge>}
                  {step.id === "review" && !step.done && completedCount >= 4 && (
                    <Badge variant="outline" className="text-xs gap-1"><Clock size={10} /> Pending</Badge>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* NPI Verification */}
        {!creds.npi_verified && (
          <Card className="border-border mb-6">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center gap-2">
                <Search size={18} className="text-primary" />
                <h3 className="font-display text-lg text-foreground">Verify Your NPI</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                We'll verify your NPI ({creds.npi_number}) against the NPPES registry.
              </p>
              {creds.npi_verification_data?.error && (
                <p className="text-sm text-destructive">{creds.npi_verification_data.error}</p>
              )}
              <Button onClick={handleVerifyNpi} disabled={verifyingNpi} className="gap-2">
                <Search size={14} />
                {verifyingNpi ? "Verifying..." : "Verify NPI"}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* BAA */}
        {creds.npi_verified && (
          <div className="mb-6">
            <BAAgreement
              onAccept={handleSignBaa}
              isLoading={signingBaa}
              accepted={!!creds.baa_accepted_at}
            />
          </div>
        )}

        {/* W-9 Upload */}
        {creds.npi_verified && !!creds.baa_accepted_at && creds.w9_status !== "uploaded" && creds.w9_status !== "verified" && (
          <Card className="border-border mb-6">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center gap-2">
                <Upload size={18} className="text-primary" />
                <h3 className="font-display text-lg text-foreground">Upload W-9 Form</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                We need your W-9 form for tax reporting purposes. We'll issue a 1099 for your earnings.
              </p>
              <div>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleW9Upload}
                  disabled={uploadingW9}
                  className="text-sm file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary file:text-primary-foreground hover:file:bg-primary/90 file:cursor-pointer"
                />
              </div>
              {uploadingW9 && <p className="text-sm text-muted-foreground">Uploading...</p>}
            </CardContent>
          </Card>
        )}

        {/* All steps done, waiting for admin */}
        {completedCount >= 4 && creds.credential_status === "pending_review" && (
          <Card className="border-primary/20 bg-primary/5">
            <CardContent className="p-6 text-center">
              <Clock size={32} className="mx-auto text-primary mb-3" />
              <h3 className="font-display text-lg text-foreground mb-2">Under Review</h3>
              <p className="text-sm text-muted-foreground">
                All onboarding steps are complete! An admin will review your application and grant access shortly.
              </p>
            </CardContent>
          </Card>
        )}

        {creds.credential_status === "rejected" && (
          <Card className="border-destructive/20 bg-destructive/5 mb-6">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle size={18} className="text-destructive" />
                <h3 className="font-display text-lg text-destructive">Application Not Approved</h3>
              </div>
              {creds.rejection_reason && (
                <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20 mb-4">
                  <p className="text-sm text-foreground font-medium mb-1">Reason:</p>
                  <p className="text-sm text-muted-foreground">{creds.rejection_reason}</p>
                </div>
              )}
              <p className="text-sm text-muted-foreground mb-4">
                You can update your credentials below and resubmit for review.
              </p>
              <CredentialSubmissionForm
                userId={user!.id}
                onSubmitted={fetchCreds}
                isResubmission
                initialValues={{
                  npiNumber: creds.npi_number,
                  licenseNumber: creds.medical_license_number,
                  licenseState: creds.license_state,
                  licenseExpiry: creds.license_expiry,
                  deaNumber: creds.dea_number || "",
                  deaExpiry: creds.dea_expiry || "",
                  specialty: creds.specialty || "",
                  bio: creds.bio || "",
                }}
              />
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ProviderOnboardingGate;
